import React, { FC, useCallback, useEffect, useState } from "react"
import { Button } from "../ui/Button"
import styled from "styled-components"
import { TextInput } from "../ui/TextInput"
import { useLicenseKeyMutation } from "../mutations"
import { motion } from "motion/react"
import { CheckCircleIcon } from "@heroicons/react/24/outline"

type LicenseFormProps = {
  onSuccess?: () => void
  onCancel?: () => void
  onAccessGranted?: () => void
}

const StyledTextInput = styled(TextInput)`
  width: 100%;
`

const ButtonVerticalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 20px;
  justify-content: center;
  align-items: center;
`

export const LicenseForm: FC<LicenseFormProps> = ({ onCancel, onSuccess, onAccessGranted }) => {
  const [licenseKey, setLicenseKey] = useState<string>("")
  const [licenseKeyValidity, setLicenseKeyValidity] = React.useState<boolean>(
    false
  )
  const [isLicenseKeyNotExist, setIsLicenseKeyNotExist] = useState<boolean>(
    false
  )
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [signupTouched, setSignupTouched] = useState<boolean>(false)
  const [fetching, setFetching] = useState<boolean>(false)

  const {
    mutateAsync,
    isLoading: isSubmitLicenseKeyLoading,
    isError: isSubmitLicenseKeyError,
    isSuccess: isSubmitLicenseKeySuccess
  } = useLicenseKeyMutation()

  const onActivate = useCallback(async () => {
    setIsLoading(true)
    setIsLicenseKeyNotExist(false)
    try {
      const res = await mutateAsync({ licenseKey })
      console.log('res', res)
      if (res.result === 'success') {
        if (onSuccess) {
          onSuccess()
        }
      }
    } catch (e) {
      setIsLicenseKeyNotExist(true)
    } finally {
      setIsLoading(false)
    }
  }, [licenseKey])

  const onGoToDashboard = useCallback(() => {
    if (fetching) {
      return
    }
    if (onAccessGranted) {
      setFetching(true)
      onAccessGranted()
    }
  }, [onAccessGranted, setFetching, fetching])

  if (isSubmitLicenseKeySuccess) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <motion.div
          initial={{
            opacity: 0,
            scale: 0.5
          }}
          animate={{
            opacity: 1,
            scale: 1
          }}
          transition={{
            type: 'spring',
            duration: 0.3
          }}
        >
          <CheckCircleIcon style={{ width: '120px' }} />
        </motion.div>
        <motion.div
          initial={{
            scale: 0.8,
            opacity: 0,
            y: -20
          }}
          animate={{
            scale: 1,
            opacity: 1,
            y: 0
          }}
          transition={{
            type: 'spring',
            duration: 0.5,
            delay: 0.7
          }}
        >
          <Button
            large
            disabled={fetching}
            onClick={() => {
              void onGoToDashboard()
            }}
            style={{ width: "250px" }}
          >
            {"Go to dashboard"}
          </Button>
        </motion.div>
      </div>
    )
  }

  return (
    <div style={{ width: "100%" }}>
      <StyledTextInput
        label="License Key"
        placeholder="Enter your license key..."
        required
        autocomplete={"off"}
        onValidityChange={setLicenseKeyValidity}
        onChange={licenseKey => {
          if (licenseKey.length > 0) {
            setSignupTouched(true)
          }
          setIsLicenseKeyNotExist(false)
          setLicenseKey(licenseKey)
        }}
        value={licenseKey}
        error={
          signupTouched && !licenseKeyValidity
            ? "License key is required"
            : isLicenseKeyNotExist
            ? "License key is invalid"
            : undefined
        }
        transparent={false}
      />
      <ButtonVerticalWrapper>
        <Button
          large
          disabled={
            isLoading || isSubmitLicenseKeyLoading || !licenseKeyValidity
          }
          onClick={() => {
            void onActivate()
          }}
          style={{ width: "250px" }}
        >
          {"Activate"}
        </Button>
        <Button
          style={{ width: "250px" }}
          large={true}
          tertiary={true}
          onClick={() => {
            if (onCancel) {
              onCancel()
            }
          }}
        >
          Cancel
        </Button>
      </ButtonVerticalWrapper>
    </div>
  )
}
